const PATTERN_COLORS = [
  '#2563eb',
  '#059669',
  '#d97706',
  '#7c3aed',
  '#db2777',
  '#0891b2',
  '#4f46e5',
  '#ca8a04',
];

export function colorForPatternId(patternId) {
  const n = Number(patternId) || 0;
  return PATTERN_COLORS[Math.abs(n) % PATTERN_COLORS.length];
}

function parseTimeParts(timeStr) {
  if (!timeStr) return null;
  const parts = String(timeStr).trim().split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { h, m };
}

/** Combina fecha YYYY-MM-DD y hora HH:MM(:SS) en Date local */
export function combineDateAndTime(dateStr, timeStr) {
  const t = parseTimeParts(timeStr);
  if (!t || !dateStr) return null;
  const [y, mo, d] = dateStr.split('-').map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d, t.h, t.m, 0);
}

function buildTimedEvent({ date, startTime, endTime, title, extendedProps, backgroundColor }) {
  const start = combineDateAndTime(date, startTime);
  if (!start) return null;
  let end = combineDateAndTime(date, endTime);
  if (!end) return null;
  if (end <= start) {
    end.setDate(end.getDate() + 1);
  }
  const id = `${extendedProps.view}-${extendedProps.entityId || 'x'}-${date}-${title}-${startTime}`;
  return {
    id,
    title,
    start,
    end,
    backgroundColor,
    borderColor: backgroundColor,
    extendedProps,
  };
}

function buildAllDayEvent({ date, title, extendedProps, backgroundColor, borderColor, classNames, display }) {
  return {
    id: `${extendedProps.view}-allday-${date}-${title}`,
    title,
    start: date,
    allDay: true,
    backgroundColor,
    borderColor: borderColor || backgroundColor,
    classNames: classNames || [],
    display: display || 'auto',
    extendedProps,
  };
}

const ABSENCE_EVENT_COLORS = {
  vacation: '#f59e0b',
  sick_leave: '#ef4444',
  maternity: '#ec4899',
  training: '#14b8a6',
  special_leave: '#a855f7',
  unpaid_leave: '#64748b',
};

function absenceEventStyle(absenceType) {
  const borderMap = {
    vacation: '#d97706',
    sick_leave: '#b91c1c',
    maternity: '#be185d',
    training: '#0f766e',
    special_leave: '#7e22ce',
    unpaid_leave: '#475569',
  };
  const backgroundColor = ABSENCE_EVENT_COLORS[absenceType] || '#7c3aed';
  return {
    backgroundColor,
    borderColor: borderMap[absenceType] || '#5b21b6',
    className: `labor-fc-absence labor-fc-absence-${absenceType || 'other'}`,
  };
}

/**
 * Día de empleado o patrón: { date, shift_name, start_time, end_time, is_working_day, is_on_absence, absence_type }
 */
export function mapDailyScheduleToEvents(days, { view, entityId, entityLabel }) {
  if (!Array.isArray(days)) return [];
  const events = [];

  days.forEach((day) => {
    if (!day?.date) return;

    if (day.is_on_absence) {
      const absenceStyle = absenceEventStyle(day.absence_type);
      events.push(
        buildAllDayEvent({
          date: day.date,
          title: day.shift_name || 'Ausencia',
          backgroundColor: absenceStyle.backgroundColor,
          borderColor: absenceStyle.borderColor,
          classNames: [absenceStyle.className],
          extendedProps: {
            view,
            entityId,
            entityLabel,
            isWorkingDay: false,
            isOnAbsence: true,
            absenceType: day.absence_type,
            plannedWorkingDay: Boolean(day.planned_working_day),
          },
        })
      );
      return;
    }

    if (!day.is_working_day) {
      events.push(
        buildAllDayEvent({
          date: day.date,
          title: day.shift_name || 'Descanso',
          backgroundColor: '#4b5563',
          extendedProps: {
            view,
            entityId,
            entityLabel,
            isWorkingDay: false,
          },
        })
      );
      return;
    }

    const title =
      entityLabel && view === 'employee'
        ? day.shift_name
        : day.shift_name || 'Trabajo';

    const ev = buildTimedEvent({
      date: day.date,
      startTime: day.start_time,
      endTime: day.end_time,
      title,
      backgroundColor: view === 'pattern' ? colorForPatternId(entityId) : '#2563eb',
      extendedProps: {
        view,
        entityId,
        entityLabel,
        isWorkingDay: true,
        shiftName: day.shift_name,
      },
    });
    if (ev) events.push(ev);
  });

  return events;
}

/**
 * Calendario de fábrica: { date, is_working_day, holiday_name, shifts: [{ pattern_id, pattern_name, start_time, end_time }] }
 * @param {object} options - { patternId?: number } filtra un solo patrón
 */
export function mapFactoryScheduleToEvents(days, { factoryId, patternId = null }) {
  if (!Array.isArray(days)) return [];
  const events = [];

  days.forEach((day) => {
    if (!day?.date) return;

    if (!day.is_working_day) {
      const label = day.holiday_name || 'No laborable';
      const isRegisteredHoliday = Boolean(day.holiday_name?.trim());
      events.push(
        buildAllDayEvent({
          date: day.date,
          title: label,
          backgroundColor: isRegisteredHoliday ? '#7c3aed' : '#6b7280',
          extendedProps: {
            view: 'factory',
            entityId: factoryId,
            isWorkingDay: false,
            holidayName: day.holiday_name || '',
            isRegisteredHoliday,
            eventDate: day.date,
          },
        })
      );
      return;
    }

    const shifts = (day.shifts || []).filter(
      (s) => !patternId || Number(s.pattern_id) === Number(patternId)
    );

    if (shifts.length === 0 && patternId) {
      return;
    }

    shifts.forEach((shift) => {
      const ev = buildTimedEvent({
        date: day.date,
        startTime: shift.start_time,
        endTime: shift.end_time,
        title: shift.pattern_name || `Patrón ${shift.pattern_id}`,
        backgroundColor: colorForPatternId(shift.pattern_id),
        extendedProps: {
          view: 'factory',
          entityId: factoryId,
          patternId: shift.pattern_id,
          patternName: shift.pattern_name,
          isWorkingDay: true,
        },
      });
      if (ev) events.push(ev);
    });
  });

  return events;
}

/** Extrae patrones únicos del calendario de fábrica para el selector */
export function extractPatternsFromFactoryDays(days) {
  const map = new Map();
  if (!Array.isArray(days)) return [];
  days.forEach((day) => {
    (day.shifts || []).forEach((s) => {
      if (s.pattern_id != null) {
        map.set(s.pattern_id, s.pattern_name || `Patrón ${s.pattern_id}`);
      }
    });
  });
  return Array.from(map.entries())
    .map(([id, name]) => ({ id: Number(id), name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
